import type IconIndexType from "owa-service/lib/contract/IconIndexType";
import type ImportanceType from "owa-service/lib/contract/ImportanceType";
import type SensitivityType from "owa-service/lib/contract/SensitivityType";
import type { DisplayString, ErrorDetails, FlaggedToken, Instrumentation, QueryAlterationType, Result, } from "@1js/3s-schema";
import type { Provenance } from "./SubstrateSearchShared";

/*
SubstrateSearchResponseV1 is the 3S V1 Api response schema for search. We define the
types here because 1JS does not export the types for V1 Api. V2 Response types
are imported from 1JS.
*/
export interface SubstrateSearchResponseV1 {
    ApiVersion: string;
    EntitySets: EntitySetV1[];
    Instrumentation: Instrumentation;
    SearchTerms: string[];
    error?: ErrorDetails; // Present when call to 3S fails
}

export interface Properties {
    HasParseException?: boolean;
    WordBreakerLanguage?: string;
}

export interface EntitySetV1 {
    QueryAlterationResponse?: QueryAlterationResponseV1;
    ResultSets: SearchResultSetV1[];
    Type?: EntitySetType;
    Properties?: Properties;
}

export interface SearchResultSetV1 {
    MoreResultsAvailable: boolean;
    Provenance: Provenance;
    QueryId: QueryId;
    Results: Result[];
    Total: number;
}

interface QueryId {
    Id: string;
}

interface RecourseQuery {
    HighlightedString: string;
    RawString: string;
}

export interface QueryAlterationResponseV1 {
    AlteredQuery: {
        AlteredQuery: DisplayString;
        FlaggedTokens: FlaggedToken[];
    };
    QueryAlterationSource?: string;
    QueryAlterationType: QueryAlterationType;
    RecourseQuery: RecourseQuery;
}

export type QueryIdV1 = {
    Id: string;
};

type EntitySetType = "Message" | "Messages" | "Conversation" | "Conversations" | "Event" | "Events" | "File" | "Files" | "People";

interface SearchResultItem {
    Categories?: string[];
    ConversationId?: SearchResultItemId;
    ConversationThreadId?: string;
    DateTimeCreated?: string;
    DateTimeLastModified?: string;
    DateTimeReceived?: string;
    DateTimeSent?: string;
    DiagnosticsItemContent?: string;
    DiagnosticsItemQuery?: string;
    DisplayBcc?: string;
    DisplayCc?: string;
    DisplayTo?: string;
    ExtendedProperty?: SearchResultExtendedPropertyType[];
    Flag?: SearchResultFlagType;
    HasAttachments?: boolean;
    Hashtags?: string[];
    HasProcessedSharepointLink?: boolean;
    IconIndex?: IconIndexType;
    Importance?: ImportanceType;
    IsDraft?: boolean;
    IsFromMe?: boolean;
    ItemClass?: string;
    ItemId?: SearchResultItemId;
    ParentFolderDisplayName?: string;
    ParentFolderId?: SearchResultFolderId;
    Preview?: string;
    SearchKey?: string;
    Sensitivity?: SensitivityType;
    Size?: number;
    SortKey?: number;
    SortOrderSource?: string;
    Subject?: string;
    ReferenceId?: string;
}

export interface SearchResultItemId {
    ChangeKey: string;
    EntryId: string;
    Id: string;
    OutlookItemId: string;
}

export interface SearchResultFolderId {
    ChangeKey?: string;
    Id: string;
}

interface SearchResultExtendedPropertyType {
    ExtendedFieldURI: string;
    Value: string;
    Values: string[];
}

export interface SearchResultFlagType {
    CompleteDate: string;
    DueDate: string;
    FlagStatus: SearchResultFlagStatus;
    StartDate: string;
}

type SearchResultFlagStatus = "NotFlagged" | "Complete" | "Flagged";

export interface SearchResultFile extends SearchResultItem {
    Author: string[];
    Created: string;
    ModifiedBy: string;
    LastModifiedTime: string;
    Title: string;
    FileExtension: string;
    UniqueId?: string;
    Id?: string;
    DocId?: string;
    // Six possible URL fields
    LinkingUrl: string;
    ServerRedirectedURL: string;
    OriginalPath: string;
    Path: string;
    DefaultEncodingUrl: string;
    ClassicAttachmentVisualizationUrl: string;
    FileName?: string; // Populated for SharePoint files
    Filename?: string; // Populated for OneDrive files
}

export interface SearchFile {
    ItemId: string;
    Author: string[];
    AuthorUpn?: string;
    Created: string;
    ModifiedBy: string;
    ModifiedByUpn?: string;
    LastModifiedTime: string;
    FileName: string;
    Title: string;
    LinkingUrl: string;
    FileExtension: string;
    InstanceKey?: string;
}

// The smaller person object that will be used in the UI
// Currently has the bare minimum fields just to get the UI working
export interface SearchPerson {
    ItemId: string;
    name?: string;
    officeLocation?: string;
    role?: string;
    email: string;
    InstanceKey: string;
}

export interface PersonPhone {
    Number: string;
    Type: "Business" | "Mobile" | "Home" | "Other";
}

export interface PersonaInferences {
    IsPersonalContact?: boolean;
}

type PeopleSubtype = "OrganizationUser" | "OrganizationContact" | "PersonalContact" | "ImplicitContact" | "ChatImplicitContact" | "Room" | "UnifiedGroup" | "PublicDistributionList" | "PersonalDistributionList" | "Guest" | "Unknown";

// Specifies all the properties of a persona search result in the network response
export interface SearchResultPersona extends SearchResultItem {
    Id?: string;
    ConcatenatedId?: string; // Available on private contacts and PDLs
    DisplayName?: string;
    GivenName?: string;
    Surname?: string;
    EmailAddresses?: string[];
    ProxyEmailAddresses?: string[];
    CompanyName?: string;
    Department?: string;
    OfficeLocation?: string;
    AdditionalOfficeLocation?: string;
    JobTitle?: string;
    ImAddress?: string;
    PeopleType?: "Person" | "Group" | "Other";
    PeopleSubtype?: PeopleSubtype;
    ADObjectId?: string;
    UserPrincipalName?: string;
    Confidence?: string; // "High" or null
    Text?: string;
    QueryText?: string; // Used for a click to query
    Score?: number;
    PropertyHits?: string[];
    AdditionalNames?: string[];
    Phones?: PersonPhone[];
    ExternalDirectoryObjectId?: string;
    PersonaId?: string;
    PersonId?: string;
    MRI?: string;
    Alias?: string;
    PDLItemId?: string; // Used for Private Distribution Lists
    Inferences?: PersonaInferences;
}

export type TabbedSearchItems = SearchFile[] | TeamsMessage[] | SearchPerson[];

export interface SenderProfileUser {
    displayName?: string;
    email?: string;
    department?: string;
    givenName?: string;
    isEnabled?: boolean;
    isMuted?: boolean;
    jobTitle?: string;
    mail?: string;
    mobile?: string;
    mri?: string;
    objectId?: string;
    role?: string;
    physicalDeliveryOfficeName?: string;
    sipProxyAddress?: string;
    surname?: string;
    telephoneNumber?: string;
    type?: string;
    userSubType?: string;
    userPrincipalName?: string;
    tenantId?: string;
}

interface Message {
    email?: string;
    messageType?: string;
    contentType?: string;
    content?: string;
    activityType?: string;
    clientMessageId?: string;
    imDisplayName?: string;
    skypeEmoteOffset?: string;
    skypeGuid?: string;
    properties?: {
        [name: string]: {};
    };
    externalId?: string;
    id?: string;
    type?: string;
    composeTime?: string;
    originalArrivalTime?: string;
    containerId?: string;
    parentMessageId?: string;
    from?: string;
    sequenceId?: number; // int64
    senderProfile?: SenderProfileUser;
    version?: number; // int64
    threadType?: string;
    amsReferences?: string[];
    isEscalationToNewPerson?: boolean;
    escalationSourceMessageChatId?: string;
    escalationSourceMessageId?: string;
    annotationsSummary?: {
        [name: string]: {};
    };
}

export interface TeamsMessage extends Message {
    displayTo: string;
    ItemId: string;
    messageType: string;
    chatName: string;
    isExpanded: boolean;
    topicGroupId: string;
    clientThreadId: string;
    InstanceKey: string;
}

export interface User {
    AadObjectId: string;
    DisplayName: string;
    Upn: string;
}

export interface SearchResultMessage extends SearchResultItem {
    ConversationIndex?: string;
    ConversationTopic?: string;
    FolderRefinerId?: string;
    From?: SearchResultRecipient;
    ImmutableId?: string;
    InternetMessageId?: string;
    IsRead?: boolean;
    MailboxGuids?: string[];
    Sender?: SearchResultRecipient;
    SenderSMTPAddress?: string;
    MentionsPreview?: MentionsPreview;
}

export interface SearchResultTeamsMessage extends SearchResultItem {
    ConversationHexId?: string;
    ConversationRestId?: string;
    ConversationIndex?: number[];
    ConversationTopic?: string;
    DocumentId?: number;
    MentionsPreview?: MentionsPreview;
    ClientConversationId?: string;
    ClientThreadId?: string;
    AnnouncementTitle?: string;
    From?: SearchResultRecipient;
    InternetMessageId?: string;
    IsRead?: boolean;
    ItemHexId?: string;
    ItemRestId?: string;
    MailboxGuids?: string[];
    NormalizedSubject?: string;
    ReceivedOrRenewTime?: string;
    Sender?: SearchResultRecipient;
    SideEffects?: number;
    WebLink?: string;
    OwnerFolderRestId?: string;
    ParentFolderDisplayName?: string;
    Extensions?: SearchResultsTeamsChatExtensions;
}

interface MentionsPreview {
    IsMentioned: boolean;
}

export interface SearchResultRecipient {
    EmailAddress: SearchResultEmailAddress;
}

interface SearchResultsTeamsChatExtensions {
    SkypeSpaces_ConversationPost_Extension_ThreadType: string;
    SkypeSpaces_ConversationPost_Extension_Topic: string;
    SkypeSpaces_ConversationPost_Extension_SkypeGroupId: string;
    SkypeSpaces_ConversationPost_Extension_ParentMessageId: string;
}

interface SearchResultEmailAddress {
    Address: string;
    EmailAddressIndex: string;
    IsDomainInOrganization: boolean;
    IsExternalSmtpAddress: boolean;
    ItemId: SearchResultItemId;
    MailboxType: string;
    Name: string;
    OriginalDisplayName: string;
    RelevanceScore: number;
    RoutingType: string;
    SipUri: string;
    Submitted: boolean;
}

export interface SearchResultConversation extends SearchResultItem {
    ConversationTopic?: string;
    FlagStatus?: SearchResultFlagStatus;
    FolderRefinerId?: string;
    From?: SearchResultRecipient;
    GlobalItemIds?: SearchResultItemId[];
    GlobalMessageCount?: number;
    GlobalUnreadCount?: number;
    HasIrm?: boolean;
    ImmutableId?: string;
    IsMentioned?: boolean;
    ItemClasses?: string[];
    ItemIds?: SearchResultItemId[];
    LastDeliveryOrRenewTime?: string;
    LastDeliveryTime?: string;
    LastModifiedTime?: string;
    MailboxGuids?: string[];
    MessageCount?: number;
    SenderSMTPAddress?: string;
    UniqueRecipients?: string[];
    UniqueSenders?: string[];
    UnreadCount?: number;
}

export interface SearchResultEvent extends SearchResultItem {
    CalendarItemType?: SearchResultCalendarItemType;
    Charm?: number;
    End?: string;
    HitHighlightedSummary?: string;
    ImmutableId?: string;
    IsAllDayEvent?: boolean;
    IsMeeting?: boolean;
    IsMeetingPollEvent?: boolean;
    IsOrganizer?: boolean;
    IsCancelled?: boolean;
    MyResponseType?: string;
    LegacyFreeBusyStatus?: SearchResultBusyType;
    Location?: string;
    Organizer?: SearchResultRecipient;
    SeriesId?: string;
    SeriesMasterItemId?: SearchResultItemId;
    Start?: string;
    UID?: string;
}

export enum SearchResultCalendarItemType {
    Single = 0,
    Occurrence = 1,
    Exception = 2,
    RecurringMaster = 3
}

export enum SearchResultBusyType {
    Free = 0,
    Tentative = 1,
    Busy = 2,
    Oof = 3,
    WorkingElsewhere = 4,
    NoData = 5
}
